import { useEffect, useState } from "react";
import { get } from "../../api";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

export default function PerformanceGraph({ embedded = false }) {
  const [data, setData] = useState([]);

  useEffect(() => {
    async function fetchData() {
      const response = await get("/api/1/jobs/performance");
      const formattedData = response.performance.map((item) => {
        const date = new Date(item.day);
        const day = date.toLocaleDateString(window.navigator.language, {
          weekday: "long",
        });

        return {
          name: day,
          ratio: item.avg_compression_ratio * 100,
          count: item.count,
        };
      });

      setData(formattedData);
    }

    fetchData();
  }, []);

  const Container = ({ children }) => (
    embedded ? <>{children}</> : (
      <div className="bg-background-elevated rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 p-6 border border-border/60">{children}</div>
    )
  );

  // Custom tooltip component with theme support
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background-elevated border border-border/60 rounded-lg shadow-xl p-4 backdrop-blur-sm">
          <p className="text-sm font-semibold text-foreground mb-3 border-b border-border/40 pb-2">{label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center justify-between gap-4 py-1">
              <div className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-xs text-muted-foreground">
                  {entry.name === "ratio" ? "Compression ratio" : "Completed jobs"}
                </span>
              </div>
              <span className="text-sm font-bold text-foreground">
                {entry.name === "ratio" ? `${entry.value.toFixed(2)}%` : entry.value}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  // Get CSS variable colors
  const getThemeColor = (varName) => {
    if (typeof window === 'undefined') return '#6366F1';
    const root = document.documentElement;
    const hsl = getComputedStyle(root).getPropertyValue(varName).trim();
    if (!hsl) {
      // Fallback colors
      if (varName === '--accent') return '#6366F1';
      if (varName === '--success') return '#10B981';
      if (varName === '--muted-foreground') return '#6B7280';
      if (varName === '--border') return '#374151';
      return '#6366F1';
    }
    
    // Convert HSL to hex for Recharts
    const [h, s, l] = hsl.split(' ').map(v => parseFloat(v));
    return `hsl(${h}, ${s}%, ${l}%)`;
  };

  const accentColor = getThemeColor('--accent');
  const successColor = getThemeColor('--success');
  const mutedColor = getThemeColor('--muted-foreground');
  const borderColor = getThemeColor('--border');
  
  const textColor = mutedColor || '#9CA3AF';
  const gridColor = borderColor || '#374151';


  return (
    <Container>
      {!embedded && <h2 className="text-xl font-semibold text-foreground mb-4">Compression performance</h2>}
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart 
            data={data}
            margin={{ top: 5, right: 5, bottom: 5, left: 0 }}
          >
            <defs>
              <linearGradient id="colorRatio" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={accentColor} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={accentColor} stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={successColor} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={successColor} stopOpacity={0}/>
              </linearGradient>
              <filter id="shadow">
                <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.3"/>
              </filter>
            </defs>
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke={gridColor}
              opacity={0.2}
              vertical={false}
            />
            <XAxis 
              dataKey="name" 
              stroke="none"
              tick={{ fill: textColor, fontSize: 12 }}
              tickLine={false}
              axisLine={{ stroke: gridColor, strokeWidth: 1.5 }}
            />
            <YAxis
              yAxisId="left"
              stroke="none"
              tick={{ fill: textColor, fontSize: 12 }}
              tickFormatter={(value) => `${value}%`}
              tickLine={false}
              axisLine={{ stroke: gridColor, strokeWidth: 1.5 }}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              stroke="none"
              tick={{ fill: textColor, fontSize: 12 }}
              tickLine={false}
              axisLine={{ stroke: gridColor, strokeWidth: 1.5 }}
            />
            <Tooltip 
              content={<CustomTooltip />}
              cursor={{ stroke: gridColor, strokeWidth: 2, strokeDasharray: '5 5' }}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="ratio"
              stroke={accentColor}
              strokeWidth={3}
              dot={{ 
                fill: accentColor, 
                strokeWidth: 2, 
                r: 5,
                stroke: '#fff',
                filter: 'url(#shadow)'
              }}
              activeDot={{ 
                r: 7, 
                strokeWidth: 2,
                stroke: '#fff',
                filter: 'url(#shadow)'
              }}
              animationDuration={1000}
              animationEasing="ease-in-out"
            />

            <Line
              yAxisId="right"
              type="monotone"
              dataKey="count"
              stroke={successColor}
              strokeWidth={3}
              strokeDasharray="5 5"
              dot={{ 
                fill: successColor, 
                strokeWidth: 2, 
                r: 4,
                stroke: '#fff',
                filter: 'url(#shadow)'
              }}
              activeDot={{ 
                r: 6, 
                strokeWidth: 2,
                stroke: '#fff',
                filter: 'url(#shadow)'
              }}
              animationDuration={1000}
              animationEasing="ease-in-out"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Container>
  );
}
